import {
  Component,
  ChangeDetectionStrategy,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { AkitaLocationQuery } from '@app/akita/api/location/state/location.query';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import {
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { CommonModule } from '@angular/common';

import { TranslocoModule } from '@ngneat/transloco';
import { Params, RouterModule } from '@angular/router';
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { PopularDevices } from '@app/akita/api/remote-config/models/popular-devices.model';
import { AkitaRemoteConfigQuery } from '@app/akita/api/remote-config/state/remote-config.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { AddIconComponent } from '@app/shared/components/icons/generated/add/add.component';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { CartOverlayDynamicService } from '@app/overlays/cart-overlay/services/cart-overlay-dynamic.service';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { logAddAccessoryToCart } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { InstallmentAmountPipe } from '@app/shared/pipes/installment-amount.pipe';

@Component({
  selector: 'app-bought-together-banner',
  templateUrl: './bought-together-banner.template.html',
  styleUrls: ['./bought-together-banner.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    InstallmentAmountPipe,
    RouterModule,
    TranslocoModule,
    PopsySkeletonComponent,
    PictureComponent,
    AmountPipe,
    AddIconComponent,
  ],
})
export class BoughtTogetherBannerComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;

  public readonly isBrowser: boolean;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly isRtlLayout$: Observable<boolean>;

  public readonly queryParams$: Observable<Params>;

  public productImage: string | null | undefined;

  public popularDevices$: Observable<PopularDevices | null>;
  public oftenTogether: Product | null = null;

  public urlFragments: string | string[];
  public country$: Observable<string>;

  public showingCartOverlay: boolean;
  private openCartOverlaySubscription?: Subscription | null;

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly analytics: GoogleAnalyticsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaLocationQuery: AkitaLocationQuery,
    private readonly akitaRemoteConfigQuery: AkitaRemoteConfigQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaCartService: AkitaCartService,
    private readonly cartOverlayDynamicService: CartOverlayDynamicService,
    private readonly akitaProductsQuery: AkitaProductsQuery
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject<void>();
    this.urlFragments = '';
    this.showingCartOverlay = false;

    this.queryParams$ = this.akitaRouterQuery.selectQueryParams();

    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.isRtlLayout$ = combineLatest([
      this.akitaRouterQuery.selectIsRtlLayout(),
      this.akitaRouterQuery.selectRouteData(),
    ]).pipe(
      map(([rtlMode, data]) => {
        if (rtlMode || data.locale === 'ar') {
          return true;
        }
        return false;
      })
    );

    this.popularDevices$ = of(null);

    this.urlFragments = this.getSearchUrlFragment();

    this.country$ = this.akitaLocationQuery
      .selectCountry()
      .pipe(map((country: string) => `${country || 'us'}`.toUpperCase()));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      this.oftenTogether = null;

      this.popularDevices$ = this.akitaRemoteConfigQuery.selectPopularDevices().pipe(
        map((popularDevices) => {
          const cat = this.product?.category || null;
          const brand = this.product?.brand || null;
          if (
            popularDevices &&
            cat &&
            brand &&
            popularDevices[cat] &&
            popularDevices[cat][brand]
          ) {
            if (popularDevices[cat][brand].oftenBoughtWith > 0) {
              this.subscriptions.add(
                this.akitaProductsService
                  .getProduct(`${popularDevices[cat][brand].oftenBoughtWith}`)
                  .pipe(takeUntil(this.destroySubject))
                  .subscribe({
                    next: (product) => {
                      if (product && product.inStock && product.id !== this.product?.id) {
                        this.oftenTogether = product;
                        this.urlFragments = this.akitaRouterQuery.getProductUrlFragment(
                          this.oftenTogether?.id || '',
                          this.oftenTogether?.titleEn || ''
                        );
                        this.changeDetector.markForCheck();
                        this.changeDetector.detectChanges();
                      }
                    },
                    error: (error: unknown) => {
                      console.log('error: ', error);
                    },
                  })
              );
            }
          }
          return popularDevices;
        })
      );
    }
  }

  public getSearchUrlFragment(): Array<string> | string {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    const translatedPath = 'about-us';
    fragment.push(translatedPath);
    return fragment;
  }

  public addBothToCart(): boolean {
    if (this.product?.id && this.oftenTogether?.id) {
      this.subscriptions.add(
        this.akitaCartService
          .addNewProductToCart(this.product)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: () => {
              if (this.oftenTogether) {
                this.akitaCartService.addNewProductToCartAsync(this.oftenTogether);
                logAddAccessoryToCart(
                  this.analytics.logEventWrapper,
                  this.oftenTogether,
                  'often-bought-together'
                );
                this.openCart();
              }
            },
            error: () => {},
          })
      );

      /* this.akitaCartService.addProductToCartAsync(this.oftenTogether, 'buy_now_button');
      this.openCart(); */
    }

    return false;
  }

  public getTransLabel(value: string): string {
    if (value === 'EXCELLENT') {
      return 'label_condition_excelent';
    } else if (value === 'GOOD') {
      return 'label_condition_good';
    } else if (value === 'FAIR') {
      return 'label_condition_fair';
    }
    return 'label_condition_excelent';
  }

  public openCart(): void {
    if (this.openCartOverlaySubscription) {
      this.openCartOverlaySubscription.unsubscribe();
    }

    const validLocale = this.akitaProductsQuery.validateLocale();
    if (validLocale.country === 'US') {
      // shopify cart opening not needed
    } else {
      this.openCartOverlaySubscription = this.cartOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.showingCartOverlay = Boolean(data);
            this.changeDetector.markForCheck();
          },
        });
      this.cartOverlayDynamicService.open({
        data: {},
        isBrowser: this.isBrowser,
      });
    }
  }

  public scrollToTop(): void {
    try {
      if (
        this.akitaRouterQuery.isBrowser &&
        window.document &&
        window.document.getElementById
      ) {
        const scrollElement = window.document.getElementById('mainAppScroll');
        if (scrollElement) {
          if (scrollElement.scrollTo) {
            scrollElement.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          } else {
            scrollElement.scrollTop = 0;
          }
        }
      }
    } catch (err) {
      /* SSR Not Supported */
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
